/**
 * LivenessCheck Component — Simplified & User-Friendly
 * 
 * 3 quick challenges with clear visual guides:
 * 1. Center face (3s)
 * 2. Blink (6s) 
 * 3. Turn head left OR right (6s, random)
 * 
 * Total time: ~15 seconds (down from ~40s)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, CheckCircle2, XCircle, Loader2, AlertTriangle, RotateCcw, Eye, ArrowLeft, ArrowRight } from 'lucide-react';
import * as faceapi from '@vladmandic/face-api';
import { compressImage } from '../lib/imageCompression';

// Challenge types (simplified)
type ChallengeType = 'center' | 'blink' | 'turn';

interface Challenge {
  type: ChallengeType;
  instruction: string;
  instructionFil: string;
  icon: string;  // emoji for visual cue
  timeout: number;
}

interface LivenessCheckProps {
  onComplete: (result: LivenessResult) => void;
  onCancel: () => void;
  language?: 'en' | 'fil';
  isAssisted?: boolean;
}

export interface LivenessResult {
  passed: boolean;
  score: number;
  selfieBlob: Blob | null;
  challenges: {
    type: ChallengeType;
    passed: boolean;
    duration: number;
  }[];
  failureReasons: string[];
}

// Eye landmark indices for 68-point model
const LEFT_EYE = [36, 37, 38, 39, 40, 41];
const RIGHT_EYE = [42, 43, 44, 45, 46, 47];

// Relaxed thresholds for mobile/low-quality cameras
const EAR_THRESHOLDS = {
  open: 0.19,
  closed: 0.15,
};

// Throttle face detection to ~5fps on mobile (200ms interval)
const DETECTION_INTERVAL_MS = 200;

function calculateEAR(landmarks: faceapi.Point[], indices: number[]): number {
  const p = indices.map(i => landmarks[i]);
  const vertical1 = Math.sqrt(Math.pow(p[1].x - p[5].x, 2) + Math.pow(p[1].y - p[5].y, 2));
  const vertical2 = Math.sqrt(Math.pow(p[2].x - p[4].x, 2) + Math.pow(p[2].y - p[4].y, 2));
  const horizontal = Math.sqrt(Math.pow(p[0].x - p[3].x, 2) + Math.pow(p[0].y - p[3].y, 2));
  return (vertical1 + vertical2) / (2.0 * horizontal);
}

function estimateHeadPose(landmarks: faceapi.Point[]): { yaw: number; pitch: number } {
  const nose = landmarks[30];
  const leftEye = landmarks[36];
  const rightEye = landmarks[45];
  const eyeCenter = { x: (leftEye.x + rightEye.x) / 2, y: (leftEye.y + rightEye.y) / 2 };
  const eyeDistance = Math.sqrt(Math.pow(rightEye.x - leftEye.x, 2) + Math.pow(rightEye.y - leftEye.y, 2));
  const yaw = ((nose.x - eyeCenter.x) / eyeDistance) * 90;
  const pitch = ((nose.y - eyeCenter.y) / eyeDistance) * 90;
  return { yaw, pitch };
}

export default function LivenessCheck({ onComplete, onCancel, language = 'en', isAssisted = false }: LivenessCheckProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'running' | 'capturing' | 'complete' | 'failed'>('loading');
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [challengeResults, setChallengeResults] = useState<LivenessResult['challenges']>([]);
  const [instruction, setInstruction] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Challenge state
  const challengeStartTime = useRef<number>(0);
  const blinkState = useRef<{ wasOpen: boolean; blinkCount: number }>({ wasOpen: true, blinkCount: 0 });
  const challengeCompleted = useRef(false);

  // Native camera fallback (for devices obstructing getUserMedia)
  const [useNativeCamera, setUseNativeCamera] = useState(isAssisted);

  // Force native mode if isAssisted prop is true
  useEffect(() => {
    if (isAssisted) {
      setUseNativeCamera(true);
    }
  }, [isAssisted]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Random turn direction
  const [turnDirection] = useState<'left' | 'right'>(() => Math.random() > 0.5 ? 'left' : 'right');

  // Build challenge list — reduced to selfie-only when isAssisted is true
  const [challenges] = useState<Challenge[]>(() => {
    if (isAssisted) {
      // Assisted mode: selfie-only (no active liveness challenges like blink/turn)
      return [{
        type: 'center' as ChallengeType,
        instruction: 'Position your face in the oval',
        instructionFil: 'Ilagay ang iyong mukha sa oval',
        icon: '👤',
        timeout: 5000,
      }];
    }
    return [
      {
        type: 'center' as ChallengeType,
        instruction: 'Position your face in the oval',
        instructionFil: 'Ilagay ang iyong mukha sa oval',
        icon: '👤',
        timeout: 4000,
      },
      {
        type: 'blink' as ChallengeType,
        instruction: 'Blink your eyes slowly',
        instructionFil: 'Dahan-dahang ikurap ang iyong mga mata',
        icon: '👁️',
        timeout: 10000,
      },
      {
        type: 'turn' as ChallengeType,
        instruction: turnDirection === 'left'
          ? 'Slowly turn your head to the LEFT'
          : 'Slowly turn your head to the RIGHT',
        instructionFil: turnDirection === 'left'
          ? 'Dahan-dahang ilingon ang ulo sa KALIWA'
          : 'Dahan-dahang ilingon ang ulo sa KANAN',
        icon: turnDirection === 'left' ? '⬅️' : '➡️',
        timeout: 10000,
      },
    ];
  });

  // Load face-api models
  useEffect(() => {
    async function loadModels() {
      try {
        setLoadingProgress(10);
        await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
        setLoadingProgress(40);
        await faceapi.nets.faceLandmark68Net.loadFromUri('/models');
        setLoadingProgress(80);
        await faceapi.nets.faceExpressionNet.loadFromUri('/models');
        setLoadingProgress(100);
        setModelsLoaded(true);
        setStatus('ready');
      } catch (err) {
        console.error('Failed to load face models:', err);
        // Don't fail immediately, allow native camera fallback
        setError('Failed to load optimization models. Please try Native Camera mode.');
        setStatus('failed');
      }
    }
    loadModels();
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStatus('running');
        challengeStartTime.current = Date.now();
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Camera access denied or not supported. Please use the Native Camera option.');
      setStatus('failed');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, []);


  const handleNativeCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setStatus('loading');
      setLoadingProgress(10);
      
      // Compress image first to avoid 413
      const compressedBlob = await compressImage(file);
      setLoadingProgress(50);

      // Load image to check for face
      const img = await faceapi.bufferToImage(compressedBlob);
      
      let detection = null;
      if (modelsLoaded) {
          const options = new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.3, inputSize: 512 });
          
          // Try original orientation
          detection = await faceapi.detectSingleFace(img, options);
          
          if (!detection) {
              // Try rotating 90, 180, 270 degrees
              console.log('Face not found in original orientation, trying rotations...');
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              if (ctx) {
                  const rotations = [90, 180, 270];
                  for (const angle of rotations) {
                      if (detection) break;
                      
                      try {
                          // Set dimensions based on rotation
                          if (angle === 180) {
                              canvas.width = img.width;
                              canvas.height = img.height;
                          } else {
                              canvas.width = img.height;
                              canvas.height = img.width;
                          }
                          
                          ctx.save();
                          ctx.translate(canvas.width / 2, canvas.height / 2);
                          ctx.rotate((angle * Math.PI) / 180);
                          ctx.drawImage(img, -img.width / 2, -img.height / 2);
                          ctx.restore();
                          
                          detection = await faceapi.detectSingleFace(canvas, options);
                          console.log(`Rotation ${angle}° detection result:`, !!detection);
                      } catch (rotErr) {
                          console.warn(`Rotation ${angle}° failed:`, rotErr);
                      }
                  }
              }
          }

          if (!detection) {
              console.warn('Face detection failed even after rotations.');
              setError('No face detected. Please ensure you are in a well-lit area and facing the camera directly.');
              setStatus('failed');
              return;
          }
      }

      onComplete({
          passed: true,
          score: 0.8, // Fallback score
          selfieBlob: compressedBlob,
          challenges: [],
          failureReasons: ['Native Camera Fallback used'],
      });

    } catch (err) {
        console.error('Native capture error:', err);
        setError('Failed to process image. Please try again.');
        setStatus('failed');
    }
  };

  // Process challenge completion
  const completeChallenge = useCallback((passed: boolean) => {
    if (challengeCompleted.current) return;
    challengeCompleted.current = true;

    const duration = Date.now() - challengeStartTime.current;
    const challenge = challenges[currentChallengeIndex];

    if (passed) {
      setChallengeResults(prev => [...prev, { type: challenge.type, passed: true, duration }]);
      setFeedbackMessage(
        currentChallengeIndex === 0 ? '✓ Face detected!' :
        currentChallengeIndex === 1 ? '✓ Blink detected!' :
        '✓ Head turn detected!'
      );

      if (currentChallengeIndex < challenges.length - 1) {
        setTimeout(() => {
          setCurrentChallengeIndex(prev => prev + 1);
          challengeStartTime.current = Date.now();
          challengeCompleted.current = false;
          blinkState.current = { wasOpen: true, blinkCount: 0 };
          setFeedbackMessage('');
        }, 800);
      } else {
        // All done — capture the selfie!
        setStatus('capturing');
      }
    } else {
      // Timeout — just record failure and move on (don't block the user)
      setChallengeResults(prev => [...prev, { type: challenge.type, passed: false, duration }]);

      if (currentChallengeIndex < challenges.length - 1) {
        setFeedbackMessage('⏳ Timed out — moving on...');
        setTimeout(() => {
          setCurrentChallengeIndex(prev => prev + 1);
          challengeStartTime.current = Date.now();
          challengeCompleted.current = false;
          blinkState.current = { wasOpen: true, blinkCount: 0 };
          setFeedbackMessage('');
        }, 1000);
      } else {
        setStatus('capturing');
      }
    }
  }, [challenges, currentChallengeIndex]);

  // Capture final selfie
  const captureSelfie = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.9);
    });

    if (!blob) return null;
    return await compressImage(blob);
  }, []);

  // Face detection loop
  useEffect(() => {
    if (status !== 'running' || !videoRef.current || !modelsLoaded) return;

    const detectFace = async () => {
      if (!videoRef.current || status !== 'running') return;

      try {
        const detection = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 }))
          .withFaceLandmarks()
          .withFaceExpressions();

        if (detection) {
          setFaceDetected(true);
          const landmarks = detection.landmarks.positions;
          const challenge = challenges[currentChallengeIndex];

          if (!challenge) return;

          // Update progress
          const elapsed = Date.now() - challengeStartTime.current;
          setProgress(Math.min(100, (elapsed / challenge.timeout) * 100));

          // Timeout
          if (elapsed > challenge.timeout) {
            completeChallenge(false);
            scheduleNext();
            return;
          }

          switch (challenge.type) {
            case 'center': {
              const box = detection.detection.box;
              const videoWidth = videoRef.current.videoWidth;
              const videoHeight = videoRef.current.videoHeight;
              const centerX = box.x + box.width / 2;
              const centerY = box.y + box.height / 2;
              const isCentered =
                Math.abs(centerX - videoWidth / 2) < videoWidth * 0.2 &&
                Math.abs(centerY - videoHeight / 2) < videoHeight * 0.25;

              if (isCentered && elapsed > 1500) {
                completeChallenge(true);
              }
              break;
            }

            case 'blink': {
              const leftEAR = calculateEAR(landmarks, LEFT_EYE);
              const rightEAR = calculateEAR(landmarks, RIGHT_EYE);
              const avgEAR = (leftEAR + rightEAR) / 2;

              const isOpen = avgEAR > EAR_THRESHOLDS.open;
              const isClosed = avgEAR < EAR_THRESHOLDS.closed;

              if (blinkState.current.wasOpen && isClosed) {
                blinkState.current.blinkCount++;
              }
              blinkState.current.wasOpen = isOpen;

              if (blinkState.current.blinkCount >= 1) {
                completeChallenge(true);
              }
              break;
            }

            case 'turn': {
              const pose = estimateHeadPose(landmarks);
              // Relaxed threshold: 15° for mobile cameras
              if (turnDirection === 'left' && pose.yaw < -15) {
                completeChallenge(true);
              } else if (turnDirection === 'right' && pose.yaw > 15) {
                completeChallenge(true);
              }
              break;
            }
          }
        } else {
          setFaceDetected(false);
        }
      } catch (err) {
        console.error('Face detection error:', err);
      }

      scheduleNext();
    };

    // Throttle: wait at least DETECTION_INTERVAL_MS between frames
    const scheduleNext = () => {
      const elapsed = Date.now() - (scheduleNext as any).lastFrame;
      const delay = Math.max(0, DETECTION_INTERVAL_MS - elapsed);
      (scheduleNext as any).lastFrame = Date.now();
      animationRef.current = window.setTimeout(() => {
        requestAnimationFrame(detectFace);
      }, delay) as unknown as number;
    };
    (scheduleNext as any).lastFrame = 0;

    detectFace();

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [status, modelsLoaded, currentChallengeIndex, challenges, completeChallenge, turnDirection]);

  // Update instruction when challenge changes
  useEffect(() => {
    if (currentChallengeIndex < challenges.length) {
      const challenge = challenges[currentChallengeIndex];
      setInstruction(language === 'fil' ? challenge.instructionFil : challenge.instruction);
    }
  }, [currentChallengeIndex, challenges, language]);

  // Handle capture completion
  useEffect(() => {
    if (status !== 'capturing') return;

    const finalize = async () => {
      const selfieBlob = await captureSelfie();
      stopCamera();

      const passedCount = challengeResults.filter(r => r.passed).length;
      const totalChallenges = challenges.length;
      const score = passedCount / totalChallenges;
      // Assisted mode: pass with 1/1. Normal: pass with at least 2/3 (66%)
      const minPassed = isAssisted ? 1 : 2;
      const passed = passedCount >= minPassed;

      const failureReasons: string[] = [];
      challengeResults.forEach(r => {
        if (!r.passed) failureReasons.push(`Timed out: ${r.type}`);
      });

      setStatus(passed ? 'complete' : 'failed');

      onComplete({
        passed,
        score,
        selfieBlob,
        challenges: challengeResults,
        failureReasons,
      });
    };

    finalize();
  }, [status, challengeResults, challenges, captureSelfie, stopCamera, onComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  // --- Render ---
  return (
    <div className="bg-gray-900 rounded-xl p-6 max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white mb-1">
          {isAssisted 
            ? (language === 'fil' ? 'Assisted Verification' : 'Assisted Verification')
            : (language === 'fil' ? 'Pagsusuri ng Liveness' : 'Quick Liveness Check')}
        </h2>
        <p className="text-gray-400 text-sm">
          {useNativeCamera 
             ? (language === 'fil' ? 'Kumuha ng maliwanag na selfie' : 'Take a clear selfie')
             : (language === 'fil'
                ? '3 mabilis na hakbang — tapos na sa 15 segundo'
                : '3 quick steps — done in 15 seconds')
          }
        </p>
      </div>

      {/* Video Container / Native Camera UI */}
      {!useNativeCamera ? (
          <div className="relative bg-gray-800 rounded-lg overflow-hidden mb-4 aspect-[4/3]">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
            <canvas ref={canvasRef} className="hidden" />

            {/* Face Guide Overlay — Oval */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`w-48 h-60 border-4 rounded-full transition-colors duration-300 ${
                faceDetected ? 'border-green-500 shadow-lg shadow-green-500/20' : 'border-blue-500/60'
              }`} />
            </div>

            {/* Challenge Visual Cue Overlay */}
            {status === 'running' && (
              <div className="absolute top-4 left-0 right-0 flex justify-center pointer-events-none">
                <div className="bg-black/70 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                  {challenges[currentChallengeIndex]?.type === 'blink' && (
                    <Eye className="w-5 h-5 text-blue-400 animate-pulse" />
                  )}
                  {challenges[currentChallengeIndex]?.type === 'turn' && turnDirection === 'left' && (
                    <ArrowLeft className="w-6 h-6 text-blue-400 animate-bounce" style={{ animationDirection: 'alternate' }} />
                  )}
                  {challenges[currentChallengeIndex]?.type === 'turn' && turnDirection === 'right' && (
                    <ArrowRight className="w-6 h-6 text-blue-400 animate-bounce" style={{ animationDirection: 'alternate' }} />
                  )}
                  <span className="text-white text-sm font-medium">
                    Step {currentChallengeIndex + 1} of {challenges.length}
                  </span>
                </div>
              </div>
            )}

            {/* Loading Overlay */}
            {status === 'loading' && (
              <div className="absolute inset-0 bg-gray-900/80 flex items-center justify-center">
                <div className="text-center w-48">
                  <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-2" />
                  <p className="text-white mb-2">Loading...</p>
                  <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300 rounded-full"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Ready Overlay */}
            {status === 'ready' && (
              <div className="absolute inset-0 bg-gray-900/80 flex items-center justify-center">
                <button
                  type="button"
                  onClick={startCamera}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  {language === 'fil' ? 'Simulan' : 'Start'}
                </button>
              </div>
            )}

            {/* Progress Bar */}
            {status === 'running' && (
              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-700">
                <div
                  className="h-full bg-green-500 transition-all duration-100"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
      ) : (
          <div className="bg-gray-800 rounded-lg p-8 mb-4 text-center">
             <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 hover:border-blue-500 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}>
                 <Camera className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                 <p className="text-white font-medium mb-2">
                     {isAssisted 
                       ? (language === 'fil' ? 'Kumuha ng Larawan' : 'Take Photo')
                       : (language === 'fil' ? 'Kumuha ng Selfie' : 'Take a Selfie')}
                 </p>
                 <p className="text-gray-400 text-sm">
                     {language === 'fil' ? 'Gamitin ang camera ng telepono' : 'Use your phone camera'}
                 </p>
                 <input
                     ref={fileInputRef}
                     type="file"
                     accept="image/*"
                     capture="user"
                     className="hidden"
                     onChange={handleNativeCapture}
                 />
             </div>
          </div>
      )}

      {/* Challenge Instruction */}
      {status === 'running' && !useNativeCamera && (
        <div className="text-center mb-4">
          {/* Step indicators */}
          <div className="flex justify-center gap-3 mb-3">
            {challenges.map((c, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  idx < currentChallengeIndex
                    ? 'bg-green-500 text-white scale-90'
                    : idx === currentChallengeIndex
                      ? 'bg-blue-500 text-white animate-pulse scale-110'
                      : 'bg-gray-700 text-gray-400'
                }`}>
                  {idx < currentChallengeIndex ? '✓' : idx + 1}
                </div>
                {idx < challenges.length - 1 && (
                  <div className={`w-6 h-0.5 ${idx < currentChallengeIndex ? 'bg-green-500' : 'bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Main instruction */}
          <p className="text-lg font-medium text-white mb-1">{instruction}</p>
          
          {/* Visual hint */}
          <p className="text-3xl mb-2">{challenges[currentChallengeIndex]?.icon}</p>

          {/* Feedback message */}
          {feedbackMessage && (
            <p className="text-green-400 text-sm font-medium animate-pulse">{feedbackMessage}</p>
          )}

          {/* Face warning */}
          {!faceDetected && !feedbackMessage && (
            <p className="text-yellow-500 text-sm flex items-center justify-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              {language === 'fil' ? 'Hindi nakita ang mukha' : 'Face not detected — move closer'}
            </p>
          )}
        </div>
      )}

      {/* Results */}
      {(status === 'complete' || status === 'failed') && (
        <div className="text-center mb-4">
          {status === 'complete' ? (
            <div className="flex items-center justify-center gap-2 text-green-500">
              <CheckCircle2 className="w-8 h-8" />
              <span className="text-lg font-medium">Liveness Verified!</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-red-500">
              <XCircle className="w-8 h-8" />
              <span className="text-lg font-medium">Verification Failed</span>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-900/30 border border-red-500 rounded-lg p-3 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {status === 'failed' && (
          <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setStatus('ready');
                  setCurrentChallengeIndex(0);
                  setChallengeResults([]);
                  setError(null);
                  setFeedbackMessage('');
                  challengeCompleted.current = false;
                  blinkState.current = { wasOpen: true, blinkCount: 0 };
                  setUseNativeCamera(false); 
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                {language === 'fil' ? 'Subukan Muli' : 'Try Again'}
              </button>

              <button
                type="button"
                onClick={() => {
                    setUseNativeCamera(true);
                    setStatus('ready');
                    setError(null);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                <Camera className="w-4 h-4" />
                {language === 'fil' ? 'Gamitin ang Native Camera' : 'Use Native Camera'}
              </button>
          </div>
        )}
        
        {/* Helper text for Native Camera when in Ready state but failed previously? No, just in Failed state */}

        <button
          type="button"
          onClick={() => {
            stopCamera();
            onCancel();
          }}
          className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
        >
          {language === 'fil' ? 'Kanselahin' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
